import { $isListNode, ListNode } from "@lexical/list";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $isHeadingNode } from "@lexical/rich-text";
import { $getSelectionStyleValueForProperty } from "@lexical/selection";
import { $findMatchingParent, $getNearestNodeOfType } from "@lexical/utils";
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  COMMAND_PRIORITY_CRITICAL,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import { useCallback, useEffect, useState } from "react";
import { isChatChannel, type ChannelName } from "../../../../../channels/types";
import {
  fontFamilyOptions,
  webFontOptions,
} from "../../../../../email-template-editor/editors/text-settings-editor";
import { $isDesignHuddleNode } from "../../../nodes/design-huddle-node";
import { $isImageNode } from "../../../nodes/image-node";
import { $getPaperColorNode } from "../../../nodes/paper-color-node";
import { $isVideoNode } from "../../../nodes/video-node";
import { $isInDataTableNode, $isInTableNode } from "../../../utils/is-in-table-node";
import { $currentAlignment } from "../alignment-buttons";
import { blockTypeToBlockInfo } from "../block-format-dropdown";
import { fontSizeOptions } from "../font-size-select";
import { getSelectionCurrentLink, linkButtonSelectionType } from "./use-link-button-on-click";

type FontOptions = keyof typeof fontFamilyOptions | keyof typeof webFontOptions;

export interface LexicalToolbarState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  listEnabled: boolean;
  blockType: keyof typeof blockTypeToBlockInfo;
  font: FontOptions;
  fontSize: keyof typeof fontSizeOptions;
  fontColour: string;
  fontBackgroundColour: string | null;
  paperColour: string | undefined;
  tableNodeSelected: boolean;
  dataTableNodeSelected: boolean;
  hasLink: boolean;
  linkDisabled: boolean;
  videoLink: boolean;
  alignment: "left" | "center" | "right" | null;
  isImageFullWidth: boolean;
}

const initialState: LexicalToolbarState = {
  isImageFullWidth: false,
  bold: false,
  italic: false,
  underline: false,
  listEnabled: false,
  blockType: "paragraph",
  font: "Arial",
  fontSize: "16px",
  fontColour: "#000",
  fontBackgroundColour: null,
  paperColour: undefined,
  tableNodeSelected: false,
  dataTableNodeSelected: false,
  hasLink: false,
  linkDisabled: false,
  videoLink: false,
  alignment: null,
};

export const useLexicalToolbar = (
  stateType: "slack" | "email" | "teams" | "text",
): LexicalToolbarState => {
  const [editor] = useLexicalComposerContext();

  const [activeEditor, setActiveEditor] = useState(editor);

  const [state, setState] = useState<LexicalToolbarState>(initialState);

  const $updateToolbar = useCallback(() => {
    const selection = $getSelection();
    const potentialImageNode = selection?.getNodes()?.[0];

    // Start with a clean slate and stack the updates.
    let updates: Partial<LexicalToolbarState> = {
      linkDisabled: false,
    };

    const alignment = $currentAlignment();
    if (alignment) {
      updates = { ...updates, alignment };
    }

    if (
      $isImageNode(potentialImageNode) ||
      $isDesignHuddleNode(potentialImageNode) ||
      $isVideoNode(potentialImageNode)
    ) {
      const hasLink = !!potentialImageNode.getHref();
      const isImageFullWidth = potentialImageNode.__proportionOfMaxWidth === 1;
      const linkDisabled = isChatChannel(stateType as ChannelName);
      const videoLink =
        $isVideoNode(potentialImageNode) ||
        ($isDesignHuddleNode(potentialImageNode) && potentialImageNode.__contentType === "video");

      setState((prev) => ({ ...prev, hasLink, isImageFullWidth, linkDisabled, videoLink }));
    }

    if ($isRangeSelection(selection)) {
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === "root"
          ? anchorNode
          : ($findMatchingParent(anchorNode, (e) => {
              const parent = e.getParent();
              return parent !== null && $isRootOrShadowRoot(parent);
            }) ?? anchorNode.getTopLevelElementOrThrow());

      const elementKey = element.getKey();
      const elementDOM = activeEditor.getElementByKey(elementKey);

      const selectionType = linkButtonSelectionType(selection);
      if (selectionType) {
        const { url } = getSelectionCurrentLink(selection, selectionType);
        updates = {
          ...updates,
          hasLink: !!url,
        };
      }

      // Update text format
      updates = {
        ...updates,
        bold: selection.hasFormat("bold"),
        italic: selection.hasFormat("italic"),
        underline: selection.hasFormat("underline"),
      };

      if (isChatChannel(stateType)) {
        const listEnabled = $isParagraphNode(element) || $isListNode(element);
        updates = { ...updates, listEnabled };
      }

      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType<ListNode>(anchorNode, ListNode);
          const type = parentList ? parentList.getListType() : element.getListType();
          if (type in blockTypeToBlockInfo) {
            updates = { ...updates, blockType: type as keyof typeof blockTypeToBlockInfo };
          }
        } else {
          const type = $isHeadingNode(element) ? element.getTag() : element.getType();
          if (type in blockTypeToBlockInfo) {
            updates = { ...updates, blockType: type as keyof typeof blockTypeToBlockInfo };
          }
        }

        if (stateType === "email") {
          const font = $getSelectionStyleValueForProperty(selection, "font-family", "Arial");
          if (font in fontFamilyOptions || font in webFontOptions) {
            updates = { ...updates, font: font as FontOptions };
          }

          const fontSize = $getSelectionStyleValueForProperty(selection, "font-size", "16px");
          if (fontSize in fontSizeOptions) {
            updates = { ...updates, fontSize: fontSize as keyof typeof fontSizeOptions };
          }

          const fontColour = $getSelectionStyleValueForProperty(selection, "color", "#000");
          updates = { ...updates, fontColour };

          const paperColour = $getPaperColorNode()?.__hex;
          updates = { ...updates, paperColour };

          const fontBackgroundColour = $getSelectionStyleValueForProperty(
            selection,
            "background-color",
            "transparent",
          );

          updates = { ...updates, fontBackgroundColour };

          const tableNodeSelected = $isInTableNode(anchorNode);
          updates = { ...updates, tableNodeSelected };

          const datatableNodeSelected = $isInDataTableNode(anchorNode);
          updates = { ...updates, dataTableNodeSelected: datatableNodeSelected };
        }
      }

      setState((prev) => ({ ...prev, ...updates }));
    }
  }, [activeEditor, stateType]);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      (_payload, newEditor) => {
        $updateToolbar();
        setActiveEditor(newEditor);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, state, $updateToolbar]);

  useEffect(() => {
    // Also we need to trigger it to refresh on the first render of the editor
    // As there have not been another update since that which created this component
    activeEditor.read(() => {
      $updateToolbar();
    });
    return activeEditor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        $updateToolbar();
      });
    });
  }, [activeEditor, $updateToolbar]);

  return state;
};
